import React, { useState, useRef, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { ReactComponent as Open } from "../../assets/images/open.svg";
import { ReactComponent as Close } from "../../assets/images/close.svg";
import { ReactComponent as Search } from "../../assets/images/search.svg";
import { ReactComponent as Message } from "../../assets/images/message.svg";
import { ReactComponent as TicketIcon } from "../../assets/images/ticket-detail.svg";
import { ReactComponent as User } from "../../assets/images/user.svg";
import { ReactComponent as Alert } from "../../assets/images/alert.svg";
import { ReactComponent as Cross } from "../../assets/images/cross.svg";
import { ReactComponent as Draft } from "../../assets/images/KnowledgeBase/draft.svg";
import { ReactComponent as Review } from "../../assets/images/KnowledgeBase/review.svg";
import { ReactComponent as Publish } from "../../assets/images/KnowledgeBase/publish.svg";
import { ReactComponent as DraftActive } from "../../assets/images/KnowledgeBase/draft-active.svg";
import { ReactComponent as ReviewActive } from "../../assets/images/KnowledgeBase/review-active.svg";
import { ReactComponent as PublishActive } from "../../assets/images/KnowledgeBase/publish-active.svg";
import { Button, Badge, Box } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectIsPopupOpen, openPopup, closePopup } from "../../Redux/toggle";
import CustomizedMenus from "../common/menuButton";
import {
  selectIsTicketDetailSidebarOpen,
  openTicketDetailSidebar,
  closeTicketDetailSidebar,
} from "../../store/features/ticketDetailSidebarSlice";
import {
  selectIsUserDetailSidebarOpen,
  openUserDetailSidebar,
  closeUserDetailSidebar,
} from "../../store/features/userDetailSidebarSlice";
import NotificationPopup from "./Notifications/NotificationPopup";
import {
  fetchNotificationUnreadCount,
} from "../../store/features/notifications";
import {
  isHeaderSearchEnabled,
  isHeaderNotificationBellEnabled,
  isHeaderInlinePageTitleEnabled,
} from "../../config/featureFlags";
import PeopleHeaderExtension from "./People/PeopleHeaderExtension";
import GroupSelectDropdown from "./ProfileComponents/GroupSelectDropdown";
import usePeoplePermissions from "../../hooks/usePeoplePermissions";
import { useAnalyticsGroupId } from "../../hooks/useAnalyticsGroupId";
import {
  isPeopleDashboardRoute,
  isCustomerDetailRoute,
  isCustomerEditRoute,
  isTeamDetailRoute,
  isTeamEditRoute,
  isAgentDetailRoute,
  isAgentEditRoute,
  isGroupDetailRoute,
  getPeopleInlineHeaderMeta,
} from "../../utils/peopleHelpers";
import { getDashboardInlineHeaderTitle, shouldShowHeaderGroupSelect } from "../../utils/dashboardHeaderMeta";
import {
  CUSTOMER_OPEN_ADD_MODAL,
  CUSTOMER_DETAIL_BAN,
  CUSTOMER_DETAIL_DELETE,
  CUSTOMER_EDIT_SAVE,
  CUSTOMER_EDIT_CANCEL,
} from "./People/Customers/customerEvents";
import {
  TEAM_OPEN_CREATE_MODAL,
  TEAM_DETAIL_ARCHIVE,
  TEAM_DETAIL_EDIT,
  TEAM_EDIT_SAVE,
  TEAM_EDIT_CANCEL,
} from "./People/Teams/teamEvents";
import {
  AGENT_OPEN_ADD_MODAL,
  AGENT_DETAIL_SUSPEND,
  AGENT_DETAIL_DELETE,
  AGENT_EDIT_SAVE,
  AGENT_EDIT_CANCEL,
} from "./People/Agents/agentEvents";
import {
  GROUP_OPEN_CREATE_MODAL,
  GROUP_DETAIL_ARCHIVE,
  GROUP_DETAIL_EDIT,
} from "./People/Groups/groupEvents";
import { TAG_OPEN_CREATE_MODAL } from "./SettingsV2/Tags/tagEvents";
import { CANNED_OPEN_CREATE_MODAL } from "./SettingsV2/Canned/cannedEvents";
import { ReactComponent as BackArrow } from "../../assets/images/People/Customers/back-arrow.svg";
import { toast } from "react-toastify";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { PlusIcon } from "../../utils/index";
import { mapApiCustomerStatus } from "../../utils/customerMappers";
import { mapApiGroupStatus } from "../../utils/groupMappers";
import { mapApiStatus as mapApiTeamStatus } from "../../utils/teamMappers";
import { isAgentSuspended } from "../../utils/agentMappers";

const DashboardHeader = () => {
  const [searchParams] = useSearchParams();
  const [hoveredItem, setHoveredItem] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const fromReviewQueue = location.state?.fromReviewQueue;
  const isEditProfilePage = location.pathname.includes("/userupdate");

  const ticketId = searchParams.get("ticketId");
  const roomId = searchParams.get("roomId");

  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage
  );
  const t = useSelector(
    (state) => state.language.translations[currentLanguage]
  );

  const userData = useSelector((state) => state.loginToken);
  const kbRole = userData?.response?.user?.kbRole?.role;
  const notificationUnread = useSelector(
    (state) => state.notifications?.unreadCount ?? 0
  );

  const isPopupOpen = useSelector(selectIsPopupOpen);
  const dispatch = useDispatch();

  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const saveMenuRef = useRef(null);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(event.target)) {
        setShowSaveMenu(false);
      }
    };

    const handleDirtyState = (event) => {
      setIsUpdateDisabled(!event.detail.isDirty);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("ARTICLE_DIRTY_STATE", handleDirtyState);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("ARTICLE_DIRTY_STATE", handleDirtyState);
    };
  }, []);

  useEffect(() => {
    setIsUpdateDisabled(true);
  }, [location.pathname]);

  useEffect(() => {
    if (!userData?.response?.user?._id) return undefined;
    dispatch(fetchNotificationUnreadCount());
  }, [dispatch, userData?.response?.user?._id]);

  const openPopupHandler = () => {
    dispatch(openPopup());
  };

  const closePopupHandler = () => {
    dispatch(closePopup());
  };



  const isTicketDetailSidebarOpen = useSelector(
    selectIsTicketDetailSidebarOpen
  );

  const isUserDetailSidebarOpen = useSelector(selectIsUserDetailSidebarOpen);

  const isTrafficPage = pathname === "/user-traffic";
  const isSettingsPage = /\/settings\/(personal|admin)(\/|$)/.test(pathname);
  const isSettingsTagManagementPage = pathname.includes(
    "/settings/personal/tag-management",
  );
  const isSettingsCannedPage = pathname.includes(
    "/settings/personal/canned-responses",
  );
  const isCustomerDetailPage = isCustomerDetailRoute(pathname);
  const isCustomerEditPage = isCustomerEditRoute(pathname);
  const isTeamDetailPage = isTeamDetailRoute(pathname);
  const isTeamEditPage = isTeamEditRoute(pathname);
  const isAgentDetailPage = isAgentDetailRoute(pathname);
  const isAgentEditPage = isAgentEditRoute(pathname);
  const isGroupDetailPage = isGroupDetailRoute(pathname);
  const peopleInlineMeta = getPeopleInlineHeaderMeta(pathname);
  const inlinePageTitle = isHeaderInlinePageTitleEnabled
    ? getDashboardInlineHeaderTitle(pathname)
    : peopleInlineMeta?.title ||
      (isSettingsPage ? "Settings" : null);
  const showInlinePageTitle = Boolean(inlinePageTitle);
  const showHeaderGroupSelect =
    isHeaderInlinePageTitleEnabled && shouldShowHeaderGroupSelect(pathname);
  const perms = usePeoplePermissions();
  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();
  const agentDetailRecord = useSelector((state) => state.peopleAgents.detail);
  const customerDetailRecord = useSelector((state) => state.peopleCustomers.detail);
  const groupDetailRecord = useSelector((state) => state.peopleGroups.detail);
  const teamDetailRecord = useSelector((state) => state.peopleTeams.detail);
  const agentDetailMatch = pathname.match(/\/people\/agents\/([^/]+)$/);
  const agentEditMatch = pathname.match(/\/people\/agents\/([^/]+)\/edit$/);
  const customerDetailMatch = pathname.match(/\/people\/customers\/([^/]+)$/);
  const groupDetailMatch = pathname.match(/\/people\/groups\/([^/]+)$/);
  const teamDetailMatch = pathname.match(/\/people\/teams\/([^/]+)$/);
  const agentRouteId = agentDetailMatch?.[1] || agentEditMatch?.[1];
  const customerRouteId = customerDetailMatch?.[1];
  const agentDetailRoleName =
    agentRouteId &&
    agentDetailRecord &&
    String(agentDetailRecord.id || agentDetailRecord._id) === String(agentRouteId)
      ? agentDetailRecord.role_name
      : null;
  const agentRoutePerms =
    agentRouteId && agentDetailRoleName
      ? perms.agents.forAgent(agentRouteId, agentDetailRoleName)
      : null;
  const agentDetailIsSuspended =
    Boolean(agentRouteId) &&
    Boolean(agentDetailRecord) &&
    String(agentDetailRecord.id || agentDetailRecord._id) === String(agentRouteId) &&
    isAgentSuspended(agentDetailRecord);
  const showAgentSuspendAction =
    isAgentDetailPage &&
    agentRoutePerms?.canSuspend &&
    !agentDetailIsSuspended;
  const customerDetailStatus =
    customerRouteId &&
    customerDetailRecord &&
    String(customerDetailRecord.id || customerDetailRecord._id) === String(customerRouteId)
      ? mapApiCustomerStatus(customerDetailRecord)
      : null;
  const showCustomerBanAction =
    isCustomerDetailPage &&
    perms.customers.canBan &&
    (customerDetailStatus === "active" || customerDetailStatus === "inactive");

  const groupRouteId = groupDetailMatch?.[1];
  const groupDetailStatus =
    groupRouteId &&
    groupDetailRecord &&
    String(groupDetailRecord.id) === String(groupRouteId)
      ? mapApiGroupStatus(
          groupDetailRecord.information?.status || groupDetailRecord.status,
          groupDetailRecord.information?.is_suspended ?? groupDetailRecord.is_suspended
        )
      : null;
  const showGroupArchiveAction =
    isGroupDetailPage && perms.groups.canArchive && groupDetailStatus === "active";

  const teamRouteId = teamDetailMatch?.[1];
  const teamDetailStatus =
    teamRouteId &&
    teamDetailRecord &&
    String(teamDetailRecord.id || teamDetailRecord._id) === String(teamRouteId)
      ? mapApiTeamStatus(teamDetailRecord.status, teamDetailRecord.is_archived)
      : null;
  const showTeamArchiveAction =
    isTeamDetailPage && perms.teams.canArchive && teamDetailStatus === "active";

  const showPeoplePrimaryAction =
    peopleInlineMeta &&
    !isCustomerDetailPage &&
    !isCustomerEditPage &&
    !isTeamDetailPage &&
    !isTeamEditPage &&
    !isAgentDetailPage &&
    !isAgentEditPage &&
    !isGroupDetailPage &&
    ((peopleInlineMeta.title === "Customers" && perms.customers.canCreate) ||
      (peopleInlineMeta.title === "Teams" && perms.teams.canCreate) ||
      (peopleInlineMeta.title === "Agents" && perms.agents.canInvite) ||
      (peopleInlineMeta.title === "Groups" && perms.groups.canCreate));

  const handleHeaderAction = (type) => {
    setShowSaveMenu(false);
    if (type === 'cancel') {
      navigate(-1);
    } else {
      window.dispatchEvent(new CustomEvent('SAVE_ARTICLE_TRIGGER', { detail: type }));
    }
  };

  return (
    <div
      className="dashboard-header"
      style={
        pathname.includes("/kb/article/new") || (pathname.includes("/kb/article/") && !pathname.includes("/kb/articles"))
          ? { position: "relative", zIndex: 999 }
          : {}
      }
    >
      <div
        className={`dashboard-container01${showInlinePageTitle ? " dashboard-container01--people" : ""}`}
      >
        {isCustomerDetailPage ||
        isCustomerEditPage ||
        isTeamDetailPage ||
        isTeamEditPage ||
        isAgentDetailPage ||
        isAgentEditPage ||
        isGroupDetailPage ? (
          <div className="dashboard-header-left-cluster">
          {
            !isCustomerDetailPage &&
            !isCustomerEditPage &&
            !isTeamDetailPage &&
            !isTeamEditPage &&
            !isAgentDetailPage &&
            !isAgentEditPage &&
            !isGroupDetailPage && (
              <div className="left-icon">
              {isPopupOpen ? (
                <Close onClick={closePopupHandler} />
              ) : (
                <Open onClick={openPopupHandler} />
              )}
            </div>
            )
          }
          
            <Box
              component="button"
              type="button"
              onClick={() => {
                if (isCustomerDetailPage || isCustomerEditPage) navigate("/people/customers");
                else if (isAgentDetailPage || isAgentEditPage) navigate("/people/agents");
                else if (isGroupDetailPage) navigate("/people/groups");
                else navigate("/people/teams");
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                p: 0,
                ml: '-4px',
              }}
            >
              <BackArrow />
              <Box
                component="span"
                sx={{
                  color: "#405261",
                  fontFamily: "Inter",
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {isCustomerDetailPage || isCustomerEditPage
                  ? "Back to Customers"
                  : isAgentDetailPage || isAgentEditPage
                    ? "Back to Agents"
                    : isGroupDetailPage
                      ? "Back to Groups"
                      : "Back to Teams"}
              </Box>
            </Box>
          </div>
        ) : showInlinePageTitle ? (
          <div className="dashboard-header-left-cluster">
            <div className="left-icon">
              {isPopupOpen ? (
                <Close onClick={closePopupHandler} />
              ) : (
                <Open onClick={openPopupHandler} />
              )}
            </div>
            <div className="people-header-inline-title">
              <h1 className="people-header-inline-heading">{inlinePageTitle}</h1>
            </div>
          </div>
        ) : (
          <div className="left-icon">
            {isPopupOpen ? (
              <Close onClick={closePopupHandler} />
            ) : (
              <Open onClick={openPopupHandler} />
            )}
          </div>
        )}
        {!isEditProfilePage && (
          <div className="right-icon">
            <div className="right-icon01">
              {isHeaderSearchEnabled && (
                <Search onClick={() => navigate('/search')} style={{ cursor: 'pointer' }} />
              )}
              {/* <Message /> */}
              {isHeaderNotificationBellEnabled && (
                <>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    invisible={notificationUnread === 0}
                    sx={{
                      "& .MuiBadge-badge": {
                        right: 7,
                        top: 6,
                        border: '1.5px solid #FFF',
                        padding: '0 4px',
                        height: '10px',
                        minWidth: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#FF4D4F',
                      }
                    }}
                    variant="dot"
                  >
                    <Alert
                      onClick={(e) => setNotificationAnchor(e.currentTarget)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Badge>
                  <NotificationPopup
                    anchorEl={notificationAnchor}
                    open={Boolean(notificationAnchor)}
                    onClose={() => setNotificationAnchor(null)}
                  />
                </>
              )}
              {isCustomerEditPage && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(CUSTOMER_EDIT_CANCEL))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      color: "#405261",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      width: 96,
                      lineHeight: "18px",
                      textTransform: "none",
                      px: 2,
                      "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(CUSTOMER_EDIT_SAVE))}
                    sx={{
                      height: 40,
                      width: 144,
                      borderRadius: "8px",
                      background: "#148DFD",
                      color: "#FFF",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      px: 2,
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      "&:hover": { background: "#0077CC" },
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
              {isCustomerDetailPage && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}>
                  {perms.customers.canEdit ? (
                  <Button
                    onClick={() => {
                      const match = pathname.match(/\/people\/customers\/([^/]+)$/);
                      if (match) navigate(`/people/customers/${match[1]}/edit`);
                    }}
                    sx={{
                      width: 64,
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      color: "#405261",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      minWidth: 64,
                      "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                    }}
                  >
                    Edit
                  </Button>
                  ) : null}
                  {showCustomerBanAction ? (
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(CUSTOMER_DETAIL_BAN))}
                    sx={{
                      width: 128,
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      color: "#FF4D4F",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      minWidth: 128,
                      "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                    }}
                  >
                    Ban Customer
                  </Button>
                  ) : null}
                  {perms.customers.canDelete ? (
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(CUSTOMER_DETAIL_DELETE))}
                    sx={{
                      width: 80,
                      height: 40,
                      borderRadius: "8px",
                      background: "#FF4D4F",
                      color: "#FFF",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      minWidth: 80,
                      boxShadow: "none",
                      "&:hover": { background: "#E63E40" },
                    }}
                  >
                    Delete
                  </Button>
                  ) : null}
                </Box>
              )}
              {isAgentEditPage && agentRoutePerms?.canEdit && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(AGENT_EDIT_CANCEL))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      color: "#405261",
                      textTransform: "none",
                      fontFamily: "Inter",
                      fontSize: 14,
                      width: 96,
                      px: 2,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(AGENT_EDIT_SAVE))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      background: "#148DFD",
                      color: "#FFF",
                      textTransform: "none",
                      fontFamily: "Inter",
                      fontSize: 14,
                      width: 144,
                      px: 2,
                      "&:hover": { background: "#0077CC" },
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
              {isAgentDetailPage && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}>
                  {agentRoutePerms?.canEdit ? (
                  <Button
                    onClick={() => {
                      const match = pathname.match(/\/people\/agents\/([^/]+)$/);
                      if (match) navigate(`/people/agents/${match[1]}/edit`);
                    }}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      color: "#405261",
                      textTransform: "none",
                      fontFamily: "Inter",
                      fontSize: 14,
                      px: 2,
                    }}
                  >
                    Edit
                  </Button>
                  ) : null}
                  {showAgentSuspendAction ? (
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(AGENT_DETAIL_SUSPEND))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      color: "#FF4D4F",
                      textTransform: "none",
                      fontFamily: "Inter",
                      fontSize: 14,
                      width: 104,
                      px: 2,
                    }}
                  >
                    Suspend
                  </Button>
                  ) : null}
                  {agentRoutePerms?.canDelete ? (
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(AGENT_DETAIL_DELETE))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      background: "#FF4D4F",
                      color: "#FFF",
                      textTransform: "none",
                      fontFamily: "Inter",
                      fontSize: 14,
                      px: 2,
                      width: 80,
                      "&:hover": { background: "#E63E40" },
                    }}
                  >
                    Delete
                  </Button>
                  ) : null}
                </Box>
              )}
              {isTeamEditPage && perms.teams.canEdit && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(TEAM_EDIT_CANCEL))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      color: "#405261",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      px: 2,
                      width: 96,
                      "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(TEAM_EDIT_SAVE))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      background: "#148DFD",
                      color: "#FFF",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      px: 2,
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      width: 144,
                      "&:hover": { background: "#0077CC" },
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
              {isTeamDetailPage && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}>
                  {showTeamArchiveAction ? (
                  <Button
                    onClick={() =>
                      window.dispatchEvent(new CustomEvent(TEAM_DETAIL_ARCHIVE))
                    }
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      color: "#405261",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      px: 2,
                      "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                    }}
                  >
                    Archive Team
                  </Button>
                  ) : null}
                  {perms.teams.canEdit ? (
                  <Button
                    onClick={() => {
                      const match = pathname.match(/\/people\/teams\/([^/]+)$/);
                      if (match) navigate(`/people/teams/${match[1]}/edit`);
                    }}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      background: "#148DFD",
                      color: "#FFF",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      px: 2,
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      "&:hover": { background: "#0077CC" },
                    }}
                  >
                    Edit Team
                  </Button>
                  ) : null}
                </Box>
              )}
              {isGroupDetailPage && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}>
                  {perms.groups.canEdit ? (
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(GROUP_DETAIL_EDIT))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      color: "#405261",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      px: 2,
                      "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                    }}
                  >
                    Edit
                  </Button>
                  ) : null}
                  {showGroupArchiveAction ? (
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent(GROUP_DETAIL_ARCHIVE))}
                    sx={{
                      height: 40,
                      borderRadius: "8px",
                      background: "#FF4D4F",
                      color: "#FFF",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      width: 104,
                      px: 2,
                      boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                      "&:hover": { background: "#E63E40" },
                    }}
                  >
                    Archive
                  </Button>
                  ) : null}
                </Box>
              )}
              {isSettingsCannedPage && (
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent(CANNED_OPEN_CREATE_MODAL))
                  }
                  sx={{
                    minWidth: 152,
                    width: "auto",
                    height: 40,
                    px: 1.75,
                    borderRadius: "8px",
                    background: "#148DFD",
                    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    "&:hover": { background: "#0f79d8" },
                    display: "inline-flex",
                    gap: "6px",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    ml: 0.5,
                  }}
                >
                  <PlusIcon />
                  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                    Create Response
                  </Box>
                </Button>
              )}
              {isSettingsTagManagementPage && (
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent(TAG_OPEN_CREATE_MODAL))
                  }
                  sx={{
                    minWidth: 112,
                    width: "136px",
                    height: 40,
                    px: 1.75,
                    borderRadius: "8px",
                    background: "#148DFD",
                    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    "&:hover": { background: "#0f79d8" },
                    display: "inline-flex",
                    gap: "6px",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    ml: 0.5,
                  }}
                >
                  <PlusIcon />
                  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                    Add Tag
                  </Box>
                </Button>
              )}
              {(showHeaderGroupSelect ||
                ((peopleInlineMeta?.title === "Agents" ||
                  peopleInlineMeta?.title === "Teams" ||
                  peopleInlineMeta?.title === "Customers") &&
                  !isAgentDetailPage &&
                  !isAgentEditPage &&
                  !isTeamDetailPage &&
                  !isTeamEditPage &&
                  !isCustomerDetailPage &&
                  !isCustomerEditPage &&
                  !isHeaderInlinePageTitleEnabled)) && (
                <Box sx={{ ml: 0.5, flexShrink: 0, position: "relative", zIndex: 102 }}>
                  <GroupSelectDropdown
                    value={selectedGroupId}
                    onSelect={setSelectedGroupId}
                  />
                </Box>
              )}
              {showPeoplePrimaryAction && (
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => {
                    if (peopleInlineMeta.title === "Customers") {
                      window.dispatchEvent(new CustomEvent(CUSTOMER_OPEN_ADD_MODAL));
                    } else if (peopleInlineMeta.title === "Teams") {
                      window.dispatchEvent(new CustomEvent(TEAM_OPEN_CREATE_MODAL));
                    } else if (peopleInlineMeta.title === "Agents") {
                      window.dispatchEvent(new CustomEvent(AGENT_OPEN_ADD_MODAL));
                    } else if (peopleInlineMeta.title === "Groups") {
                      window.dispatchEvent(new CustomEvent(GROUP_OPEN_CREATE_MODAL));
                    } else {
                      toast.info(`Open ${peopleInlineMeta.primaryLabel} (demo)`);
                    }
                  }}
                  sx={{
                    width: peopleInlineMeta.title === "Customers" ? 176 : peopleInlineMeta.title === "Teams" ? 160 : peopleInlineMeta.title === "Agents" ? 152 : 168,
                    minWidth: peopleInlineMeta.title === "Customers" ? 176 : peopleInlineMeta.title === "Teams" ? 160 : peopleInlineMeta.title === "Agents" ? 152 : 168,
                    height: "40px",
                    borderRadius: "8px",
                    background: "#148DFD",
                    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                    textTransform: "none",
                    "&:hover": { background: "#0f79d8" },
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    ml: 0.5,
                  }}
                >
                  <PlusIcon />
                  {peopleInlineMeta.primaryLabel}
                </Button>
              )}
              {/* Check if we are on the New Article or Article Detail page to show Save button */}
              {/* Hide entire section for viewers (kbRole === 'viewer') */}
              {(pathname.includes("/kb/article/new") || (pathname.includes("/kb/article/") && !pathname.includes("/kb/articles"))) && kbRole !== 'viewer' && (
                <div style={{ display: "flex", alignItems: "center", marginLeft: '20px', gap: '8px' }}>
                  <Button
                    onClick={() => handleHeaderAction('cancel')}
                    style={{
                      width: '80px',
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid #D5DCE1',
                      background: '#FFF',
                      boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
                      color: '#FF4D4F',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none',
                      cursor: 'pointer',
                      '&:hover': { border: '1px solid #c5ced6', background: '#FFF', }
                    }}
                  >
                    Cancel
                  </Button>

                  {pathname.includes("/kb/article/new") || fromReviewQueue ? (
                    <div ref={saveMenuRef} style={{ position: 'relative' }}>
                      <Button
                        onClick={() => setShowSaveMenu(!showSaveMenu)}

                        sx={{
                          width: '96px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#148DFD',
                          color: '#FFF',
                          border: 'none',
                          textTransform: 'none',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',

                          boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
                          '&:hover': { background: '#0077cc' }
                        }}
                      >
                        Save as
                        <svg width="6" height="6" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: showSaveMenu ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                          <path d="M1 1L6 6L11 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Button>

                      {showSaveMenu && (
                        <div style={{
                          position: 'absolute',
                          top: '48px',
                          right: 0,
                          width: '184px',
                          padding: '8px 0',
                          borderRadius: '8px',
                          border: '1px solid #D5DCE1',
                          background: '#FFF',
                          boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
                          zIndex: 1000,
                          display: 'flex',
                          flexDirection: 'column',
                          color: '#405261',
                          textAlign: 'left',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                        }}>
                          <div
                            onClick={() => handleHeaderAction('draft')}
                            style={{
                              padding: '8px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: hoveredItem === 'draft' ? '#F9FAFA' : 'transparent',
                              color: hoveredItem === 'draft' ? '#061522' : 'inherit'
                            }}
                            onMouseEnter={() => setHoveredItem('draft')}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            {hoveredItem === 'draft' ? <DraftActive /> : <Draft />} Save as Draft
                          </div>
                          <div
                            onClick={() => handleHeaderAction('submitForReview')}
                            style={{
                              padding: '8px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: hoveredItem === 'submitForReview' ? '#F9FAFA' : 'transparent',
                              color: hoveredItem === 'submitForReview' ? '#061522' : 'inherit'
                            }}
                            onMouseEnter={() => setHoveredItem('submitForReview')}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            {hoveredItem === 'submitForReview' ? <ReviewActive /> : <Review />} Submit for Review
                          </div>
                          {/* Only show Publish option for editors (not for contributors) */}
                          {kbRole === 'editor' && (
                            <div
                              onClick={() => handleHeaderAction('publish')}
                              style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: hoveredItem === 'publish' ? '#F9FAFA' : 'transparent',
                                color: hoveredItem === 'publish' ? '#061522' : 'inherit'
                              }}
                              onMouseEnter={() => setHoveredItem('publish')}
                              onMouseLeave={() => setHoveredItem(null)}
                            >
                              {hoveredItem === 'publish' ? <PublishActive /> : <Publish />} Publish
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleHeaderAction('update')}
                      disabled={isUpdateDisabled}
                      sx={{
                        width: '96px',
                        height: '40px',
                        borderRadius: '8px',
                        background: isUpdateDisabled ? '#f9fafa' : '#148DFD',
                        color: '#FFF',
                        border: 'none',
                        textTransform: 'none',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontWeight: 500,
                        border: isUpdateDisabled ? '1px solid #D5DCE1' : 'none',
                        cursor: isUpdateDisabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
                        '&:hover': { background: isUpdateDisabled ? '#f9fafa' : '#0077cc' }
                      }}
                    >
                      Update
                    </Button>
                  )}
                </div>
              )}

              {pathname === "/ticketsection" && ticketId && !isTicketDetailSidebarOpen && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <p className="inline"></p>
                  <button
                    className="user-detail-btn"
                    style={{ width: "152px" }}
                    onClick={() =>
                      isTicketDetailSidebarOpen
                        ? dispatch(closeTicketDetailSidebar())
                        : dispatch(openTicketDetailSidebar())
                    }
                  >
                    <TicketIcon /> Ticket Details
                  </button>
                </div>
              )}
              {pathname === "/Live" && roomId && !isUserDetailSidebarOpen && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <p className="inline"></p>
                  <button
                    className="user-detail-btn"
                    onClick={() =>
                      isUserDetailSidebarOpen
                        ? dispatch(closeUserDetailSidebar())
                        : dispatch(openUserDetailSidebar())
                    }
                  >
                    <User /> User Details
                  </button>
                </div>
              )}
              {false && isTrafficPage && !isUserDetailSidebarOpen && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <p className="inline"></p>
                  <button
                    className="user-detail-btn"
                    onClick={() =>
                      isUserDetailSidebarOpen
                        ? dispatch(closeUserDetailSidebar())
                        : dispatch(openUserDetailSidebar())
                    }
                  >
                    <User /> User Details
                  </button>
                </div>
              )}
              {(isTicketDetailSidebarOpen && ticketId) && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <p className="inline"></p>
                  <button
                    className="user-detail-btn"
                    style={{
                      color: "#FF4D4F",
                    }}
                    onClick={() => dispatch(closeTicketDetailSidebar())}
                  >
                    <Cross /> Close Details
                  </button>
                </div>
              )}
              {(isUserDetailSidebarOpen && (pathname === "/Live" && roomId)) && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <p className="inline"></p>
                  <button
                    className="user-detail-btn"
                    style={{
                      color: "#FF4D4F",
                    }}
                    onClick={() => dispatch(closeUserDetailSidebar())}
                  >
                    <Cross /> Close Details
                  </button>
                </div>
              )}
              {/* 
              {pathname !== "/Live" ||
                (pathname !== "/ticketsection" && (
                  <>
                    <p className="inline"></p>
                    <div className="dashboardheader-right-container">
                      <CustomizedMenus />
                      <div className="right-button">
                        <p className="conv-text">{t.CONVERSATIONS}</p>
                        <p className="number-con">0</p>
                      </div>
                    </div>
                  </>
                ))} 
              */}
            </div>
          </div>
        )}
      </div>
      {isPeopleDashboardRoute(pathname) &&
        !isCustomerDetailPage &&
        !isCustomerEditPage &&
        !isTeamDetailPage &&
        !isTeamEditPage && <PeopleHeaderExtension />}
    </div>
  );
};

export default DashboardHeader;
